/**
 * Learning Report Panel Component
 *
 * Displays learning system analysis results:
 * - Successful patterns
 * - Failed patterns
 * - Insights and recommendations
 * - RSI/MACD/EMA analysis
 *
 * Phase 5 Implementation
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import {
  getLearningReport,
  processUnanalyzedTrades,
  type LearningReport,
  type LearningPattern,
  type IndicatorStat,
} from "@app/services/learningApi";
import { logger } from "@app/services/logger";
import "./learningReportPanel.css";

interface LearningReportPanelProps {
  isVisible: boolean;
}

export function LearningReportPanel({ isVisible }: LearningReportPanelProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [analysisDays, setAnalysisDays] = useState(90);

  const {
    data: report,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["learningReport", analysisDays],
    queryFn: () => getLearningReport(analysisDays),
    enabled: isVisible,
    staleTime: 60000, // 1 minute
  });

  const processMutation = useMutation({
    mutationFn: processUnanalyzedTrades,
    onSuccess: (data) => {
      logger.info(`Processed ${data.processed_count} unanalyzed trades`);
      // Invalidate report query to refresh
      queryClient.invalidateQueries({ queryKey: ["learningReport"] });
    },
    onError: (error: Error) => {
      logger.error("Error processing trades:", error);
      alert(`Failed to process trades: ${error.message}`);
    },
  });

  if (!isVisible) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="learning-report-panel">
        <Skeleton style={{ height: "400px" }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="learning-report-panel learning-report-panel--error">
        <h3>Error Loading Report</h3>
        <p>{error instanceof Error ? error.message : "Unknown error"}</p>
        <Button onClick={() => refetch()} variant="neutral">
          Retry
        </Button>
      </Card>
    );
  }

  if (!report || report.total_trades_analyzed === 0) {
    return (
      <Card className="learning-report-panel learning-report-panel--empty">
        <h3>No Learning Data Available</h3>
        <p>Not enough completed trades for analysis.</p>
        <p>Wait for more trades to close or check if learning is enabled in settings.</p>
      </Card>
    );
  }

  return (
    <div className="learning-report-panel">
      {/* Header */}
      <Card className="learning-report-panel__header">
        <div className="learning-report-panel__header-top">
          <div>
            <h2>Learning Analysis Report</h2>
            <p className="learning-report-panel__meta">
              Generated: {new Date(report.generated_at).toLocaleString("en-US")} |{" "}
              Period: {report.analysis_period_days} days |{" "}
              Trades Analyzed: {report.total_trades_analyzed}
            </p>
          </div>
          <div className="learning-report-panel__controls">
            <select
              value={analysisDays}
              onChange={(e) => setAnalysisDays(Number(e.target.value))}
              className="learning-report-panel__days-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
            <Button
              onClick={() => refetch()}
              variant="neutral"
              disabled={isLoading}
            >
              Refresh
            </Button>
            <Button
              onClick={() => processMutation.mutate(analysisDays)}
              variant="primary"
              disabled={processMutation.isPending}
            >
              {processMutation.isPending ? "Processing..." : "Process Unanalyzed"}
            </Button>
          </div>
        </div>
      </Card>

      {/* Insights */}
      {report.insights.length > 0 && (
        <Card className="learning-report-panel__insights">
          <h3>Key Insights</h3>
          <ul className="learning-report-panel__insights-list">
            {report.insights.map((insight, index) => (
              <li key={index} className="learning-report-panel__insight-item">
                {insight}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Recommendations */}
      {report.recommendations.length > 0 && (
        <Card className="learning-report-panel__recommendations">
          <h3>Recommendations (Manual Review)</h3>
          <ul className="learning-report-panel__recommendations-list">
            {report.recommendations.map((rec, index) => (
              <li key={index} className="learning-report-panel__recommendation-item">
                {rec}
              </li>
            ))}
          </ul>
          <p className="learning-report-panel__warning">
            Note: These are suggestions only. Review before making changes.
          </p>
        </Card>
      )}

      {/* Successful Patterns */}
      {report.successful_patterns.length > 0 && (
        <Card className="learning-report-panel__patterns">
          <h3>Top Successful Patterns</h3>
          <div className="learning-report-panel__patterns-list">
            {report.successful_patterns.slice(0, 5).map((pattern, index) => (
              <PatternCard key={index} pattern={pattern} type="success" />
            ))}
          </div>
        </Card>
      )}

      {/* Failed Patterns */}
      {report.failed_patterns.length > 0 && (
        <Card className="learning-report-panel__patterns learning-report-panel__patterns--failed">
          <h3>Patterns to Avoid</h3>
          <div className="learning-report-panel__patterns-list">
            {report.failed_patterns.slice(0, 5).map((pattern, index) => (
              <PatternCard key={index} pattern={pattern} type="danger" />
            ))}
          </div>
        </Card>
      )}

      {/* Indicator Analysis */}
      <div className="learning-report-panel__indicators">
        <Card className="learning-report-panel__indicator">
          <h3>RSI Range Analysis</h3>
          {Object.keys(report.rsi_ranges).length > 0 ? (
            <IndicatorTable data={report.rsi_ranges} />
          ) : (
            <p>No RSI data available</p>
          )}
        </Card>

        <Card className="learning-report-panel__indicator">
          <h3>MACD Signal Analysis</h3>
          {Object.keys(report.macd_patterns).length > 0 ? (
            <IndicatorTable data={report.macd_patterns} />
          ) : (
            <p>No MACD data available</p>
          )}
        </Card>

        <Card className="learning-report-panel__indicator">
          <h3>Trend Direction Analysis</h3>
          {Object.keys(report.ema_patterns).length > 0 ? (
            <IndicatorTable data={report.ema_patterns} />
          ) : (
            <p>No trend data available</p>
          )}
        </Card>
      </div>

      {/* TA Confidence Analysis */}
      {Object.keys(report.ta_confidence_patterns).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>TA Confidence Level Analysis</h3>
          <p className="learning-report-panel__section-desc">
            Win rates grouped by the confidence level of the technical analysis engine
            (high / medium / low).
          </p>
          <IndicatorTable data={report.ta_confidence_patterns} />
        </Card>
      )}

      {/* TA Signal Strength Analysis */}
      {Object.keys(report.ta_signal_strength_patterns).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>Signal Strength Analysis</h3>
          <p className="learning-report-panel__section-desc">
            Performance breakdown by signal strength (weak / medium / strong).
          </p>
          <IndicatorTable data={report.ta_signal_strength_patterns} />
        </Card>
      )}

      {/* TA Overall Score Ranges */}
      {Object.keys(report.ta_overall_score_ranges).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>Overall Score Range Analysis</h3>
          <p className="learning-report-panel__section-desc">
            Win rates for each overall score bucket computed by the TA engine.
          </p>
          <IndicatorTable data={report.ta_overall_score_ranges} />
        </Card>
      )}

      {/* MTF Analysis */}
      {Object.keys(report.ta_mtf_analysis).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>Multi-Timeframe Analysis</h3>
          <p className="learning-report-panel__section-desc">
            How trend alignment and consensus signal across timeframes affect trade outcomes.
          </p>
          <MtfAnalysisTable data={report.ta_mtf_analysis} />
        </Card>
      )}

      {/* Breakout Analysis */}
      {Object.keys(report.ta_breakout_analysis).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>Breakout Pattern Analysis</h3>
          <p className="learning-report-panel__section-desc">
            Win rates by breakout direction and strength detected before entry.
          </p>
          <IndicatorTable data={report.ta_breakout_analysis} />
        </Card>
      )}

      {/* Volume Analysis */}
      {Object.keys(report.ta_volume_patterns).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>Volume Score Analysis</h3>
          <p className="learning-report-panel__section-desc">
            Trade performance grouped by volume score ranges at entry time.
          </p>
          <IndicatorTable data={report.ta_volume_patterns} />
        </Card>
      )}

      {/* Volatility Analysis */}
      {Object.keys(report.ta_volatility_patterns).length > 0 && (
        <Card className="learning-report-panel__section">
          <h3>Volatility Score Analysis</h3>
          <p className="learning-report-panel__section-desc">
            How market volatility at entry affects trade outcomes.
          </p>
          <IndicatorTable data={report.ta_volatility_patterns} />
        </Card>
      )}
    </div>
  );
}

// Pattern Card Component
interface PatternCardProps {
  pattern: LearningPattern;
  type: "success" | "danger";
}

function PatternCard({ pattern, type }: PatternCardProps): JSX.Element {
  const winRatePercent = (pattern.win_rate * 100).toFixed(1);
  const confidencePercent = (pattern.confidence * 100).toFixed(0);

  return (
    <div className={`pattern-card pattern-card--${type}`}>
      <div className="pattern-card__header">
        <span className="pattern-card__type">{pattern.pattern_type}</span>
        <span className="pattern-card__value">{pattern.pattern_value}</span>
      </div>
      <div className="pattern-card__stats">
        <div className="pattern-card__stat">
          <span className="pattern-card__stat-label">Win Rate</span>
          <span className={`pattern-card__stat-value pattern-card__stat-value--${type}`}>
            {winRatePercent}%
          </span>
        </div>
        <div className="pattern-card__stat">
          <span className="pattern-card__stat-label">Occurrences</span>
          <span className="pattern-card__stat-value">{pattern.occurrence_count}</span>
        </div>
        <div className="pattern-card__stat">
          <span className="pattern-card__stat-label">Profit Factor</span>
          <span className="pattern-card__stat-value">{pattern.profit_factor.toFixed(2)}</span>
        </div>
        <div className="pattern-card__stat">
          <span className="pattern-card__stat-label">Confidence</span>
          <span className="pattern-card__stat-value">{confidencePercent}%</span>
        </div>
      </div>
      <div className="pattern-card__details">
        <span>W: {pattern.win_count} / L: {pattern.loss_count}</span>
        <span>Avg Profit: +{pattern.avg_profit.toFixed(2)}%</span>
        <span>Avg Loss: {pattern.avg_loss.toFixed(2)}%</span>
      </div>
    </div>
  );
}

// Indicator Table Component
interface IndicatorTableProps {
  data: Record<string, IndicatorStat>;
}

function IndicatorTable({ data }: IndicatorTableProps): JSX.Element {
  const sortedEntries = Object.entries(data).sort(
    (a, b) => b[1].win_rate - a[1].win_rate
  );

  return (
    <table className="indicator-table">
      <thead>
        <tr>
          <th>Range/Signal</th>
          <th>Win Rate</th>
          <th>Trades</th>
          <th>Net PnL</th>
        </tr>
      </thead>
      <tbody>
        {sortedEntries.map(([key, values]) => (
          <tr key={key}>
            <td>{key}</td>
            <td className={values.win_rate >= 0.6 ? "indicator-table__positive" : values.win_rate <= 0.4 ? "indicator-table__negative" : ""}>
              {(values.win_rate * 100).toFixed(1)}%
            </td>
            <td>{values.total_trades}</td>
            <td className={values.net_pnl >= 0 ? "indicator-table__positive" : "indicator-table__negative"}>
              {values.net_pnl >= 0 ? "+" : ""}{values.net_pnl.toFixed(2)}%
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// MTF Analysis Table Component
interface MtfAnalysisTableProps {
  data: Record<string, Record<string, IndicatorStat>>;
}

function MtfAnalysisTable({ data }: MtfAnalysisTableProps): JSX.Element {
  const sections = Object.entries(data);

  return (
    <div className="mtf-analysis">
      {sections.map(([sectionName, sectionData]) => (
        <div key={sectionName} className="mtf-analysis__section">
          <h4 className="mtf-analysis__section-title">
            {sectionName === "trend_alignment" ? "Trend Alignment" : "Consensus Signal"}
          </h4>
          <table className="indicator-table">
            <thead>
              <tr>
                <th>Value</th>
                <th>Win Rate</th>
                <th>Trades</th>
                <th>Net PnL</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sectionData)
                .sort((a, b) => b[1].win_rate - a[1].win_rate)
                .map(([key, values]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td className={values.win_rate >= 0.6 ? "indicator-table__positive" : values.win_rate <= 0.4 ? "indicator-table__negative" : ""}>
                      {(values.win_rate * 100).toFixed(1)}%
                    </td>
                    <td>{values.total_trades}</td>
                    <td className={values.net_pnl >= 0 ? "indicator-table__positive" : "indicator-table__negative"}>
                      {values.net_pnl >= 0 ? "+" : ""}{values.net_pnl.toFixed(2)}%
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
